// OGO Notary Blog — "Make Our Notary Life Easier"
// Each article is a structured document with SEO metadata + a body block array.
// Body block shapes:
//   { type: "p", text: "..." }
//   { type: "h2", text: "..." }
//   { type: "h3", text: "..." }
//   { type: "ul", items: ["...", "..."] }
//   { type: "ol", items: ["...", "..."] }
//   { type: "callout", title: "...", text: "..." }
//   { type: "quote", text: "...", cite: "..." }
//
// SLUG rules — kebab-case, keyword-rich, permanent.

import { IMG } from "@/constants/images";

export const BLOG_CATEGORIES = {
  gear: { label: "Gear & Field Kit", color: "text-[#D4AF37]" },
  practice: { label: "Best Practices", color: "text-[#D4AF37]" },
  ethics: { label: "Ethics & Elder Care", color: "text-[#D4AF37]" },
  ron: { label: "RON & Technology", color: "text-[#D4AF37]" },
  business: { label: "Growing Your Business", color: "text-[#D4AF37]" },
  compliance: { label: "Compliance", color: "text-[#D4AF37]" },
};

export const BLOG_ARTICLES = [
  {
    slug: "top-10-mobile-notary-field-kit-essentials",
    title:
      "10 Practical Essentials for Building a Professional Mobile Notary Field Kit",
    seoTitle:
      "10 Practical Essentials for a Professional Mobile Notary Field Kit | OGO Notary",
    description:
      "A practical, faith-grounded guide to building a professional mobile notary field kit — bag, scanner, dual-tray printer, power station, journal, and more. OGO Tested and OGO Pick recommendations by Dr. Gregory Byers.",
    keywords:
      "mobile notary field kit, notary bag, notary scanner, dual tray laser printer notary, portable power station notary, notary journal, mobile notary supplies, signing agent gear, notary trunk organizer, OGO Notary, Dr. Gregory Byers",
    category: "gear",
    author: "Dr. Gregory Byers",
    publishedAt: "2026-02-14",
    updatedAt: "2026-02-14",
    readMinutes: 12,
    heroImage: IMG.mobileVisit,
    ideaListUrl: "https://amzn.to/4xjQ1ZX",
    excerpt:
      "The best time to discover what you need is before you need it. A practical, faith-grounded framework for the mobile notary field kit — ten essentials, two kits, and a system built around service.",
    body: [
      {
        type: "p",
        text: "There is an old lesson I learned over decades of working around government contracts, international operations, documentation, deadlines, and environments where preparation mattered: The best time to discover what you need is before you need it. That lesson applies just as well to mobile notary work. A professional mobile notary field kit isn't about filling your vehicle with every gadget Amazon sells. It's about thinking ahead: What ordinary problem could keep me from serving this signer well—and what can I reasonably do beforehand to prevent it? Maybe it's a dead phone. Maybe you need a stable writing surface. Maybe documents need to stay organized and protected while you're moving between appointments. Maybe equipment needs power when an outlet isn't conveniently available. Or maybe the problem is much simpler: you're standing beside your vehicle thinking, I know I packed that somewhere... Preparation matters. And at OGO Notary, we try to look at preparation through a larger lens.",
      },

      { type: "h2", text: "The GIVE Framework" },
      {
        type: "p",
        text: "Everything we build at OGO Notary is intended to pass through four questions:",
      },
      {
        type: "ul",
        items: [
          "G — GIVE: Does this allow us to give back through service to others?",
          "I — IMPACT: Can it positively impact someone's business, themselves, their family, or their community?",
          "V — VALUE: Are we creating genuine value rather than simply creating content?",
          "E — EDUCATE & EMPOWER: Does this information help someone become more capable, confident, and prepared—and perhaps equip them to pay that help forward?",
        ],
      },
      {
        type: "callout",
        title: "Our faith sits around all of it.",
        text: "We believe: What We Do Matters & Ripples Into Eternity.",
      },
      {
        type: "p",
        text: "That's also why you'll see two different labels on the products in this guide.",
      },
      {
        type: "callout",
        title: "✓ OGO TESTED",
        text: "These are products or product types I personally own, use, or have directly tested. When I discuss my experience with them, I'm speaking from firsthand use.",
      },
      {
        type: "callout",
        title: "★ OGO PICK",
        text: "These are products we've selected because their features appear useful for the job we're discussing. They should not be interpreted as products I've personally tested. That distinction matters to me. If I'm recommending something based on research, I'll tell you. If I'm talking from personal experience, I'll tell you that too.",
      },

      { type: "h2", text: "Think in Two Kits, Not One" },
      {
        type: "p",
        text: "Before getting to the ten items, here's the organizing principle behind the entire setup. A mobile notary doesn't necessarily need to carry everything through the client's front door. I prefer thinking about the mobile office in two layers:",
      },
      { type: "h3", text: "The Carry-In Kit" },
      {
        type: "p",
        text: "This contains what you reasonably expect to need at the signing location: documents, journal where applicable, commissioned notary tools, pens, laptop or tablet when appropriate, charging equipment, writing surface, and other appointment-specific supplies.",
      },
      { type: "h3", text: "The Vehicle Support Kit" },
      {
        type: "p",
        text: "This is your mobile base camp. Larger equipment and backup supplies can remain securely organized in the vehicle when appropriate: printer, power station, paper, document storage, charging cables and other backup equipment. That distinction can make your working bag lighter while keeping additional resources reasonably close at hand. With that in mind, here are our ten practical field-kit essentials.",
      },

      { type: "h2", text: "1. A Professional Mobile Notary Bag" },
      { type: "badge", kind: "tested", detail: "VANKEAN Laptop Bag" },
      {
        type: "p",
        text: "Your primary bag is the center of the carry-in system. It should provide enough organization that you don't have to empty half the bag to find a charging cable, pen, document or accessory. I personally use a VANKEAN laptop bag, and what matters to me isn't simply that it carries a laptop. I want dedicated places for the different pieces of equipment and supplies I may need. For a mobile notary, consider room for:",
      },
      {
        type: "ul",
        items: [
          "Laptop or tablet",
          "Documents and folders",
          "Charging cables",
          "Power bank",
          "Pens and basic supplies",
          "Journal when applicable",
          "Small accessories",
          "Personal/business essentials",
        ],
      },
      {
        type: "p",
        text: "The particular bag matters less than developing a repeatable place for everything.",
      },
      {
        type: "p",
        text: "OGO Tested: I own and use the VANKEAN bag.",
      },
      {
        type: "cta",
        label: "View the VANKEAN Bag on Amazon",
        href: "https://amzn.to/4cXFJGx",
        testid: "amazon-cta-vankean-bag",
      },

      { type: "h2", text: "2. A Portable Document Scanner" },
      { type: "badge", kind: "tested", detail: "Primary Scanner" },
      { type: "badge", kind: "pick", detail: "Alternative Scanner" },
      {
        type: "p",
        text: "Depending on the type of notary/signing work you perform, document scanning can become an important part of your workflow. For example, certain assignments may require scanbacks or electronic delivery of completed documents according to the hiring party's instructions. The features I'd consider include:",
      },
      {
        type: "ul",
        items: [
          "Duplex scanning",
          "Reliable document feeding",
          "Compact footprint",
          "Reasonable scanning speed",
          "Compatibility with your laptop/workflow",
          "Easy transport and setup",
        ],
      },
      {
        type: "p",
        text: "This is also an excellent example of why we're providing alternatives. The most expensive scanner isn't automatically the right scanner for every notary. Someone doing frequent signing-agent work may prioritize speed and automatic document feeding. Someone with lighter scanning needs may value portability and price more heavily.",
      },
      {
        type: "cta",
        label: "View the Primary Scanner on Amazon",
        href: "https://amzn.to/4xQ8oWe",
        testid: "amazon-cta-scanner-primary",
      },
      {
        type: "cta",
        label: "View Alternative Scanner on Amazon",
        href: "https://amzn.to/4xjMRFz",
        testid: "amazon-cta-scanner-alternative",
      },

      { type: "h2", text: "3. A Dual-Tray Laser Printer" },
      { type: "badge", kind: "tested" },
      {
        type: "p",
        text: "For notaries who perform work requiring document-package printing, a dependable laser printer can become part of the business infrastructure. A dual-tray configuration is particularly useful when assignments contain both letter- and legal-size pages. Rather than thinking of the printer as something that necessarily follows you through every client's door, I prefer thinking of it as part of the mobile support office. Depending on your particular business model, that might mean printing before leaving home or having appropriate printing capability available as part of your field setup. Features worth considering include:",
      },
      {
        type: "ul",
        items: [
          "Letter and legal paper capability",
          "Dual paper trays",
          "Dependable laser printing",
          "Reasonable print speed",
          "Toner availability and cost",
          "Connectivity appropriate for your workflow",
        ],
      },
      {
        type: "p",
        text: "Most importantly, understand the requirements of the companies and assignments you accept before investing heavily in equipment.",
      },
      {
        type: "p",
        text: "OGO Tested: This category includes equipment I have personally used.",
      },
      {
        type: "cta",
        label: "View the Dual-Tray Laser Printer on Amazon",
        href: "https://amzn.to/4xdhxYZ",
        testid: "amazon-cta-dual-tray-printer",
      },

      { type: "h2", text: "4. Portable Power Station" },
      { type: "badge", kind: "pick" },
      {
        type: "p",
        text: "Mobile work has one universal weakness: Electricity. Laptop batteries die. Phones need charging. Equipment needs power. And the outlet you expected to use isn't always where you expected it to be. A portable power station can provide another layer of resilience for notaries who operate heavily from their vehicles or regularly carry powered equipment. Consider:",
      },
      {
        type: "ul",
        items: [
          "Battery capacity",
          "AC outlet capability",
          "USB/USB-C outputs",
          "Maximum continuous output",
          "Size and weight",
          "Charging time",
          "Whether its output is appropriate for the equipment you intend to power",
        ],
      },
      {
        type: "p",
        text: "The goal isn't to build a power plant in your trunk. It's simply to ask: \"If I lose convenient access to electricity, what parts of my business stop working?\" Then plan accordingly.",
      },
      {
        type: "p",
        text: "OGO Pick: Selected as a potentially useful mobile-office option; not represented here as personally tested.",
      },
      {
        type: "cta",
        label: "View the Portable Power Station on Amazon",
        href: "https://amzn.to/4c6tY0c",
        testid: "amazon-cta-power-station",
      },

      { type: "h2", text: "5. Document Organizer / Weather-Resistant Case" },
      { type: "badge", kind: "pick" },
      {
        type: "p",
        text: "Paper is still at the center of a great deal of notary work. And paper doesn't particularly care that it's raining. A dedicated document case or organizer helps separate important paperwork from everything else you're carrying. Consider a system that helps distinguish: Documents going IN from Documents coming OUT. That simple habit can reduce confusion when handling multiple appointments. Depending upon your work, you may also want separation for originals, signer copies, completed documents, receipts and administrative materials. For mobile professionals working in Florida—or anywhere weather can change quickly—some degree of water resistance is worth considering as well.",
      },
      {
        type: "cta",
        label: "View the Document Organizer on Amazon",
        href: "https://amzn.to/4zPjPzk",
        testid: "amazon-cta-document-organizer",
      },

      { type: "h2", text: "6. A Professional Notary Journal" },
      { type: "badge", kind: "tested" },
      {
        type: "p",
        text: "A well-designed journal can provide an organized record of your notarial activity where required or appropriate. One important point: journal requirements vary by jurisdiction and by the type of notarization being performed. For example, Florida's traditional notary law does not generally require a journal, although the Governor's reference manual says a notary may consider keeping one as a protective record. So this isn't a universal statement that every notary everywhere is legally required to use this particular journal. It is a preparedness recommendation. If you use a journal, look for something that fits your state's requirements and your actual workflow.",
      },
      {
        type: "p",
        text: "OGO Tested: I have personally used this journal/product.",
      },
      {
        type: "cta",
        label: "View the Notary Journal on Amazon",
        href: "https://amzn.to/4qvZLO2",
        testid: "amazon-cta-notary-journal",
      },

      { type: "h2", text: "7. Power Bank / Mobile Charging Kit" },
      { type: "badge", kind: "pick" },
      {
        type: "p",
        text: "Your phone isn't just a phone anymore. For a mobile professional, it may also be your:",
      },
      {
        type: "ul",
        items: [
          "GPS",
          "Calendar",
          "Client communication",
          "Email",
          "Appointment confirmation",
          "Camera where appropriate",
          "Business applications",
          "Emergency communication",
        ],
      },
      {
        type: "p",
        text: "A dead phone can therefore become a business problem surprisingly quickly. A quality power bank and dedicated charging cables are inexpensive insurance against that problem. I'd consider keeping the mobile-business charging kit separate from your everyday personal charger. That way you're not asking: \"Did I remember to grab my charger?\" It simply lives in the kit.",
      },
      {
        type: "cta",
        label: "View the Power Bank on Amazon",
        href: "https://amzn.to/3UiSLrO",
        testid: "amazon-cta-power-bank",
      },

      { type: "h2", text: "8. Mobile Signing Clipboard / Writing Surface" },
      { type: "badge", kind: "pick" },
      {
        type: "p",
        text: "Not every signing happens around a beautiful conference-room table. Sometimes the available writing surface is small, cluttered, uncomfortable—or simply nonexistent. A sturdy clipboard or portable writing surface gives you another option. It's particularly useful when your work takes you into homes, hospitals, senior-care environments or other locations that weren't designed around document signings. Look for something sturdy enough to provide a comfortable writing surface without becoming another bulky piece of equipment you hate carrying. This isn't glamorous gear. That's precisely why it can be useful.",
      },
      {
        type: "cta",
        label: "View the Mobile Writing Surface on Amazon",
        href: "https://amzn.to/4wPHSvh",
        testid: "amazon-cta-clipboard",
      },

      { type: "h2", text: "9. Portable Lighting / Flashlight" },
      { type: "badge", kind: "pick" },
      {
        type: "p",
        text: "This one is easy to overlook until the first time you need it. A compact light can help with:",
      },
      {
        type: "ul",
        items: [
          "Evening appointments",
          "Finding equipment in the vehicle",
          "Navigating poorly lit areas",
          "Reading small details when additional illumination is helpful",
          "General roadside preparedness",
        ],
      },
      {
        type: "p",
        text: "I prefer equipment that can serve more than one purpose. A good compact light isn't really a \"notary gadget.\" It's simply practical equipment for someone whose office sometimes happens to be wherever the next appointment takes them.",
      },
      {
        type: "cta",
        label: "View the Portable Lighting on Amazon",
        href: "https://amzn.to/4xSGbxR",
        testid: "amazon-cta-flashlight",
      },

      { type: "h2", text: "10. Mobile Notary Trunk Organizer" },
      { type: "badge", kind: "tested" },
      {
        type: "p",
        text: "This may be my favorite addition to the list because it ties the entire system together. Your vehicle shouldn't become a rolling junk drawer. A trunk organizer lets you establish designated places for the Vehicle Support Kit:",
      },
      {
        type: "ul",
        items: [
          "Backup supplies",
          "Paper",
          "Cables",
          "Power equipment",
          "Document cases",
          "Printer-related supplies where appropriate",
          "Weather/safety items",
          "Other business equipment",
        ],
      },
      {
        type: "p",
        text: "I personally use a trunk organizer, and the real benefit isn't the organizer itself. It's the system it creates. When every item has a home, replenishing the field kit becomes easier. You can quickly see what's missing before leaving for an appointment. That's preparation—not gadget collecting.",
      },
      {
        type: "p",
        text: "OGO Tested: I personally use this product/category as part of my organization system.",
      },
      {
        type: "cta",
        label: "View the Trunk Organizer on Amazon",
        href: "https://amzn.to/4c5ygoy",
        testid: "amazon-cta-trunk-organizer",
      },

      { type: "h2", text: "Don't Forget the Basics" },
      {
        type: "p",
        text: "These ten products aren't the entire notary office. Your actual appointment kit may also require things such as your properly commissioned notary seal/stamp, appropriate certificates or forms, pens, paper, identification-related resources, commission information, instructions from the hiring party and other supplies dictated by your jurisdiction and the particular assignment. Those are professional requirements first and shopping opportunities second. Always follow the laws and rules of your commissioning jurisdiction and the lawful requirements applicable to the particular notarial act.",
      },

      { type: "h2", text: "Build a System, Not Just a Shopping List" },
      {
        type: "p",
        text: "Here's the bigger lesson. Buying ten products doesn't make someone a prepared mobile notary. Systems do. Give every important piece of equipment a permanent home. After an appointment, return it to that location. Establish minimum quantities for consumable supplies. Recharge electronics on a schedule. Check equipment periodically. Before leaving for an appointment, use a checklist rather than memory. And periodically empty the whole system and ask: What am I carrying that I never use? What did I need recently that I didn't have? What needs replacing? What could make serving my next signer easier? That's how a field kit evolves with your business.",
      },

      { type: "h2", text: "Shop the Complete OGO Notary Mobile Field Kit" },
      {
        type: "p",
        text: "We've collected the products discussed in this article into one Amazon Idea List so you don't have to hunt through the article for every item.",
      },
      {
        type: "cta",
        variant: "prominent",
        label: "Shop All 10 Mobile Notary Essentials on Amazon",
        href: "https://amzn.to/4xjQ1ZX",
        testid: "amazon-cta-idea-list",
      },
      {
        type: "p",
        text: "Some items in the collection are OGO TESTED through firsthand ownership/use. Others are OGO PICKS selected because we believe their features may be useful for mobile notary work. Always evaluate products for your own business needs, budget and applicable requirements.",
      },

      { type: "h2", text: "One Final Thought" },
      {
        type: "p",
        text: "There is a temptation in business to believe professionalism comes from having more. More equipment. More technology. More clients. More followers. More revenue. I've come to believe something different. Professionalism is ultimately about service. The bag, scanner, printer, power bank and everything else on this list are simply tools. Their purpose is to help us arrive prepared so we can focus our attention where it belongs: on the person we're there to serve. That's what GIVE means to us. Give. Impact. Value. Educate & Empower. Help someone become a little better prepared today. Maybe they'll help someone else tomorrow. Because What We Do Matters & Ripples Into Eternity. And sometimes our responsibility is simply to do the work in front of us faithfully. Give, and God takes care of tomorrow.",
      },

      {
        type: "callout",
        title: "Educational Disclaimer",
        text: "This article provides general educational and business-preparation information and is not legal advice. Notary laws, journal requirements, certificate requirements, identification standards, fees and procedures vary by jurisdiction. Always follow the laws and official guidance applicable to your commission and seek appropriate professional guidance when necessary.",
      },
      {
        type: "callout",
        title: "Affiliate Disclosure",
        text: "As an Amazon Associate I earn from qualifying purchases. Product links identified as affiliate links may generate a commission for OGO Notary at no additional cost to you. Amazon requires affiliate disclosures to be clear and conspicuous near affiliate links, which is why I've included #CommissionsEarned with the product recommendations rather than burying the relationship in fine print.",
      },
    ],
  },

  {
    slug: "5-signing-table-mistakes-that-cost-notaries-their-reputation",
    title:
      "5 Signing Table Mistakes That Cost New Notaries Their Reputation",
    seoTitle:
      "5 Signing Table Mistakes That Cost New Notaries Their Reputation | OGO Notary",
    description:
      "Five real signing-table mistakes that quietly kill a new notary's career — and the specific habits a 45-year field veteran uses to avoid them. Learn what lenders actually notice.",
    keywords:
      "notary signing agent mistakes, loan signing mistakes, mobile notary errors, NSA best practices, notary signing table, signing agent tips",
    category: "practice",
    author: "Dr. Gregory Byers",
    publishedAt: "2026-02-14",
    updatedAt: "2026-02-14",
    readMinutes: 7,
    heroImage: IMG.signingTable,
    excerpt:
      "New notaries rarely lose their careers to a single dramatic failure. They lose it to five small, repeatable mistakes at the table. Here is exactly how to spot each one — and the discipline that keeps you off the do-not-call list.",
    body: [
      {
        type: "p",
        text: "A notary's reputation is built one signing at a time and lost the same way. Lenders, title companies, and signing services do not usually issue a formal complaint when a job goes sideways. They quietly stop calling. If your volume has slowed and you cannot tell why, one of these five mistakes is almost certainly on the list.",
      },
      { type: "h2", text: "Mistake #1 — Arriving Under-Prepared" },
      {
        type: "p",
        text: "You cannot walk into a refinance signing without knowing whether the loan is a purchase, a rate-and-term refi, a cash-out, or a HELOC. Each has different signer expectations. Read the closing disclosure and the note before you leave your office. If a signer asks a basic question about their payment change and you fumble the answer, they lose confidence — and the loan officer hears about it within an hour.",
      },
      { type: "h2", text: "Mistake #2 — Correcting Documents Improperly" },
      {
        type: "p",
        text: "Never scratch out and initial a printed name, a date, or an address on a recordable document without written authorization from the lender. Small corrections that seem harmless — a wrong middle initial, a swapped address — often require a full page reprint or a formal correction agreement. The lender's underwriter will catch it. The signer will not remember they asked you to fix it. You will.",
      },
      { type: "h2", text: "Mistake #3 — Talking Past the Notary Line" },
      {
        type: "p",
        text: "The single fastest way for a signing agent to earn a complaint is to explain, interpret, or advise on the terms of a loan. You are certified to guide the signer to the signature line, verify identity, and notarize the required acts. You are not a mortgage broker, an attorney, or a financial advisor. When a signer asks, 'Is this a good rate?' the correct answer is, 'That is a question for your loan officer — let me get them on the phone right now.'",
      },
      {
        type: "callout",
        title: "The nine-word rule",
        text: "\"I can tell you what it says, not what it means.\" Repeat as needed.",
      },
      { type: "h2", text: "Mistake #4 — Sloppy Notarial Acts" },
      {
        type: "p",
        text: "An acknowledgment is not a jurat. A copy certification is not a signature witness. Each notarial act has a specific certificate wording, a specific verification standard, and specific requirements about oaths. A lender or county recorder that catches a mismatched certificate will bounce the entire package — and your name is on the log. Know the four core acts in your state and never guess.",
      },
      { type: "h2", text: "Mistake #5 — Poor Post-Signing Communication" },
      {
        type: "p",
        text: "The signing is not finished when the pen goes down. It is finished when the documents are in the title company's hands, the signing service has been confirmed of drop-off, and the tracking number is logged. A signing agent who disappears between the appointment and the courier drop is a signing agent who gets replaced. Text a scan of the final package to the loan officer inside 30 minutes of leaving the table. It costs nothing and it wins repeat business.",
      },
      { type: "h2", text: "The Discipline That Prevents All Five" },
      {
        type: "p",
        text: "Federal document work taught me that quality is a system, not a personality trait. Build a pre-signing checklist, a table-side checklist, and a post-signing checklist — and follow them like they are non-negotiable. When one signing looks the same as the last, so does your reputation.",
      },
    ],
  },

  {
    slug: "elder-care-notary-ethics-when-to-walk-away",
    title:
      "Elder Care Notary Ethics: The Moment You Should Walk Away",
    seoTitle:
      "Elder Care Notary Ethics — When to Walk Away From a Signing | OGO Notary",
    description:
      "How to spot coercion, undue influence, and capacity issues at an elder care notary signing — and the exact wording to use when you decline the notarial act. A field-tested ethics guide.",
    keywords:
      "elder care notary, senior notary ethics, notary capacity, undue influence notary, notary refuse signing, Florida notary elder abuse",
    category: "ethics",
    author: "Dr. Gregory Byers",
    publishedAt: "2026-02-14",
    updatedAt: "2026-02-14",
    readMinutes: 9,
    heroImage: IMG.eldercareHero,
    excerpt:
      "Elder care notary work is the most rewarding — and the most ethically demanding — corner of the profession. Here is how a 45-year veteran spots coercion, capacity issues, and the moment integrity requires you to decline the act.",
    body: [
      {
        type: "p",
        text: "Elder care signings — a Power of Attorney in a hospital room, a Trust amendment in an assisted living apartment, a Living Will beside a hospice bed — are sacred work. They are also the highest-risk notarial acts you will ever perform. A signing that goes wrong in this arena is not a corrected page or a bounced package. It is a family battle in probate court, and your journal entry will be Exhibit A.",
      },
      { type: "h2", text: "The Two Questions a Notary Must Silently Ask" },
      {
        type: "p",
        text: "Every elder care signing turns on two questions the notary must answer privately, without asking permission from anyone in the room: Does the signer have the capacity to understand what they are signing? And are they signing of their own free will? If either answer is anything other than a firm yes, the notarial act does not happen today.",
      },
      { type: "h2", text: "Signs of Capacity Concerns" },
      {
        type: "ul",
        items: [
          "The signer cannot state, in their own words, what the document does.",
          "The signer cannot recall the name of the person they are naming as agent, trustee, or beneficiary.",
          "The signer's answers are inconsistent — they name a different person as agent than the one written on the document.",
          "The signer is heavily medicated, disoriented to time or place, or drifting in and out of the conversation.",
          "A family member answers questions on the signer's behalf without being asked.",
        ],
      },
      { type: "h2", text: "Signs of Undue Influence" },
      {
        type: "ul",
        items: [
          "A family member insists on being present in the room during the notarial act after being asked to step out.",
          "The signer looks to another person before answering basic questions.",
          "The document was prepared by, or benefits, the person who arranged the appointment.",
          "The signer is being asked to change beneficiaries, transfer assets, or grant new authority to someone they only recently met.",
          "The signer expresses reluctance, fear, or confusion when the influencing party is present, but relaxes when they leave the room.",
        ],
      },
      {
        type: "callout",
        title: "The one-minute private conversation",
        text: "Before you notarize, ask everyone except the signer to leave the room for one minute. If that request is refused, the signing does not happen. That refusal is your answer.",
      },
      { type: "h2", text: "The Exact Wording When You Decline" },
      {
        type: "p",
        text: "Declining a notarial act does not require an accusation. It requires calm, professional clarity. I use this phrasing in the field, and it has never failed me:",
      },
      {
        type: "quote",
        text: "\"Based on my professional judgment as a Florida-commissioned notary public, I am not able to complete the notarial act today. I am happy to return at another time when we can meet under different circumstances. This is not a legal opinion — only my required duty under Florida Statute 117.\"",
        cite: "Field-tested decline script — Dr. Gregory Byers",
      },
      {
        type: "p",
        text: "Log the refusal in your journal with the date, time, location, parties present, and the specific reason for declining. That entry is your legal defense.",
      },
      { type: "h2", text: "What You Do Not Do" },
      {
        type: "ul",
        items: [
          "You do not report to Adult Protective Services from the signing table. You leave, then evaluate whether a mandatory reporting duty applies under state law.",
          "You do not argue with the family or the facility staff. You are professional, brief, and gone.",
          "You do not accept partial payment for a travel visit if you also perform any notarial act. Never mix a partial notarization into a compromised signing.",
        ],
      },
      { type: "h2", text: "Why Elder Care Notary Work Matters Anyway" },
      {
        type: "p",
        text: "The signings I decline are rare. The signings I complete — a widow finally putting her estate in order, a Marine at hospice signing a Power of Attorney for his son, a woman with early-stage dementia executing a Living Will while she is still clear — those are the moments that make this work sacred. Built on Experience. Grounded in Faith. If you cannot hold both of those tensions at the same time, elder care signings are not your calling.",
      },
    ],
  },

  {
    slug: "ron-in-florida-what-signing-agents-should-know-2026",
    title: "RON in Florida — What Every Signing Agent Should Know in 2026",
    seoTitle:
      "RON in Florida 2026 — Signing Agent Guide to Remote Online Notarization | OGO Notary",
    description:
      "A field guide to Remote Online Notarization (RON) in Florida for signing agents and mobile notaries in 2026. Statutes, technology, ID verification, and the RON jobs actually paying right now.",
    keywords:
      "RON Florida, Remote Online Notarization Florida, Florida RON statutes, RON signing agent, online notary Florida, RON platforms 2026",
    category: "ron",
    author: "Dr. Gregory Byers",
    publishedAt: "2026-02-14",
    updatedAt: "2026-02-14",
    readMinutes: 10,
    heroImage: IMG.remoteLaptop,
    excerpt:
      "Remote Online Notarization in Florida grew up in 2025 — and 2026 is the year signing agents who refuse to adopt it get left behind. Here is what has changed, what actually pays, and how to get on the RON platforms lenders trust.",
    body: [
      {
        type: "p",
        text: "Florida authorized Remote Online Notarization in 2020 under Chapter 117 of the Florida Statutes. Five years later, it is not a novelty — it is a mainstream closing tool. Title companies, lenders, and corporate legal departments now expect signing agents who can handle both mobile and RON work. If you are only offering in-person mobile signings in 2026, you are competing for a shrinking slice of the market.",
      },
      { type: "h2", text: "What Florida RON Actually Requires" },
      {
        type: "ul",
        items: [
          "A separate RON commission — you must be registered as an online notary with the Florida Department of State, in addition to your traditional notary commission.",
          "A $25,000 RON errors-and-omissions bond, in addition to the standard $7,500 notary bond.",
          "An approved audio-video platform that records the entire session and stores it for at least ten years.",
          "Two forms of identity proofing: knowledge-based authentication (KBA) plus credential analysis of a government ID.",
          "A tamper-evident electronic seal and electronic signature.",
        ],
      },
      { type: "h2", text: "The Platforms Lenders Actually Use" },
      {
        type: "p",
        text: "Do not sign up for a RON platform because a Facebook group told you to. Ask three title companies in your market which RON platform they use before you pay a subscription. Common Florida platforms in 2026 include NotaryCam, OneNotary, Nexsys Clear Sign, and Proof (formerly Notarize). Some are free to notaries and charge the signer; some charge notaries a monthly fee. Choose the one your lenders actually accept.",
      },
      {
        type: "callout",
        title: "The most common RON rejection",
        text: "A signer who cannot pass KBA at 8:15pm on a Friday night. Have a backup plan — usually a rescheduled session in daylight hours with better internet.",
      },
      { type: "h2", text: "What RON Signings Pay in Florida" },
      {
        type: "p",
        text: "RON signing fees have compressed since 2022. A refinance RON pays between sixty-five and one hundred fifteen dollars depending on the platform and the direct client. Purchases and reverse mortgages remain premium at one twenty-five to two hundred. The economics only work if you schedule several RON sessions in a block, use dual monitors, and never chase small-fee sessions.",
      },
      { type: "h2", text: "Where RON Beats Mobile Every Time" },
      {
        type: "ul",
        items: [
          "Snowbirds — a Michigan family closing on a Melbourne Beach condo can meet you at 9am your time from their kitchen table up north.",
          "Investor closings — busy commercial signers value the twenty-minute Zoom over a two-hour meeting.",
          "Corporate document signings — a director on a business trip needs one document notarized before Monday. RON solves it in ten minutes.",
          "Estate planning follow-ups — an out-of-state adult child witnessing a parent's document remotely, still recorded and defensible.",
        ],
      },
      { type: "h2", text: "The Skills That Make a Great RON Notary" },
      {
        type: "p",
        text: "The technology is only one third of the job. The other two thirds are the exact skills a great mobile notary already has: patience with the signer, calm under a technical hiccup, and a signing-table pace even when you are on a screen. If you are already excellent in person, RON is a matter of certification and gear — not personality.",
      },
      { type: "h2", text: "Getting Started" },
      {
        type: "p",
        text: "The Florida Department of State posts the RON application on notaries.dos.myflorida.com. Complete a state-approved RON training course (the NNA and several private trainers offer these), buy the two required bonds, register a platform, and test the entire flow end-to-end before you accept a paid session. My own first RON was a family favor at no charge — do the same. The first live session should never be the first live session.",
      },
    ],
  },

  {
    slug: "building-a-referral-network-that-feeds-your-notary-business",
    title:
      "Building a Referral Network That Feeds Your Notary Business for Life",
    seoTitle:
      "Building a Notary Referral Network That Lasts | OGO Notary",
    description:
      "The exact referral network strategy a 45-year professional uses to keep a mobile notary calendar full — without spending on ads. Title companies, elder care, estate attorneys, and more.",
    keywords:
      "notary referral network, notary marketing, signing agent business, mobile notary leads, notary business growth, notary networking",
    category: "business",
    author: "Dr. Gregory Byers",
    publishedAt: "2026-02-14",
    updatedAt: "2026-02-14",
    readMinutes: 8,
    heroImage: IMG.realEstateSigning,
    excerpt:
      "The most reliable signing calendar is not built on ads or Google reviews — it is built on ten well-chosen referral partners who never stop calling. Here is how to find them, earn them, and keep them.",
    body: [
      {
        type: "p",
        text: "The mobile notaries I know with the fullest calendars do not spend a dollar on paid ads. They spend on relationships. The strongest notary business in Brevard County looks the same as the strongest small business anywhere else — ten to twenty people know your name, trust your work, and think of you first when their client needs a notary. Everything else is noise.",
      },
      { type: "h2", text: "The Ten Referral Partners Worth Cultivating" },
      {
        type: "ol",
        items: [
          "Title company processors — the humans behind the emails who assign signings. Meet them in person once a quarter.",
          "Estate planning attorneys — one solid relationship replaces twenty cold leads.",
          "Elder law attorneys — often overlapping, often more emotionally engaged with the family than a general estate attorney.",
          "Hospice social workers — they will call you for Powers of Attorney and Living Wills at short notice for years.",
          "Assisted living facility administrators — they see a rotating cast of families every month who need documents notarized.",
          "Reverse mortgage loan officers — an underserved specialty with steady, higher-fee closings.",
          "Realtors serving snowbirds and investors — RON referrals that never touch your car.",
          "CPAs — surprise. Tax season alone generates a wave of authorization letters, entity resolutions, and IRS forms.",
          "Local wedding photographers and florists — for the wedding officiant side of the business.",
          "Other notaries — the ones who trust your work will refer overflow to you every busy season.",
        ],
      },
      { type: "h2", text: "How You Actually Earn Those Ten" },
      {
        type: "p",
        text: "You earn a referral partner the same way you earn a friend — you show up on time, you never make them look bad, and you say thank you. Specifically:",
      },
      {
        type: "ul",
        items: [
          "Respond to every text or email inside one business hour. Reliability is the entire product.",
          "Send a handwritten card after your first job with each new partner. Yes, in 2026. It works.",
          "Never mention the client's business back to the partner unless the partner asks. Discretion is currency.",
          "When you make an error, own it fast and offer to correct it at no charge. Partners keep referring notaries who make mistakes gracefully.",
          "Send a small handwritten thank-you at the end of the year with a modest gift — a coffee card, a Christmas ornament, a hand-lettered note. Do not send generic bulk gifts.",
        ],
      },
      {
        type: "callout",
        title: "The one habit that compounds",
        text: "After every signing, I text the loan officer or referring attorney a two-line note: 'Signing complete. Documents en route. Signer was well cared for.' It takes ninety seconds and it never stops working.",
      },
      { type: "h2", text: "The Marketing You Do Not Need" },
      {
        type: "p",
        text: "You do not need a Facebook page with daily posts. You do not need a paid Google Ads budget. You do not need a TikTok. You need a professional website that answers three questions in five seconds — what services you offer, what territory you cover, and how to reach you — and ten relationships that keep calling. Everything else is a distraction from the ten.",
      },
      { type: "h2", text: "The Long Game" },
      {
        type: "p",
        text: "Referral networks compound like interest. Year one is quiet. Year two is steady. Year three is when the phone rings on its own. Show up for ten people, week after week, and they will build the calendar for you.",
      },
    ],
  },

  {
    slug: "glba-compliance-plain-english-signing-agents",
    title:
      "GLBA Compliance in Plain English — What NSA Signing Agents Must Actually Do",
    seoTitle:
      "GLBA Compliance for Signing Agents (Plain English 2026) | OGO Notary",
    description:
      "A plain-English GLBA compliance guide for NNA-certified signing agents. What the Gramm-Leach-Bliley Act really requires, how it applies to loan document handling, and the exact habits that keep you compliant.",
    keywords:
      "GLBA compliance signing agent, GLBA NNA, notary signing agent NPI, loan signing GLBA, signing agent privacy, NPI compliance",
    category: "compliance",
    author: "Dr. Gregory Byers",
    publishedAt: "2026-02-14",
    updatedAt: "2026-02-14",
    readMinutes: 7,
    heroImage: IMG.notaryStamp,
    excerpt:
      "The Gramm-Leach-Bliley Act sounds like a law school seminar. It is actually the simplest and most important compliance rule every NSA signing agent must live by — explained in plain English by a 45-year federal document veteran.",
    body: [
      {
        type: "p",
        text: "The Gramm-Leach-Bliley Act — GLBA — is a 1999 federal privacy law. If that already sounds boring, stay with me. GLBA is the single most important compliance framework governing what a Notary Signing Agent (NSA) is allowed to do with the loan documents you touch every day. The NNA background screening you paid for exists because of GLBA. So does the certification renewal every year. Understanding GLBA in one sitting is the fastest way to level up as a professional.",
      },
      { type: "h2", text: "What GLBA Actually Says (in one sentence)" },
      {
        type: "p",
        text: "Financial institutions — banks, credit unions, mortgage lenders — must protect the nonpublic personal information (NPI) of their customers. When a lender hires a signing agent to bring loan documents to a borrower, you become a service provider under GLBA. You inherit the same obligation to protect that data.",
      },
      { type: "h2", text: "What is NPI, Exactly?" },
      {
        type: "ul",
        items: [
          "Social Security numbers",
          "Dates of birth",
          "Bank account numbers and routing numbers",
          "Loan balances, credit scores, and payment histories",
          "Signatures and initials tied to a financial account",
          "The mere fact that a specific person is a customer of a specific lender",
        ],
      },
      { type: "h2", text: "The Five Habits That Keep a Signing Agent Compliant" },
      { type: "h3", text: "1. Secure printing" },
      {
        type: "p",
        text: "Print in a locked office or vehicle-based printer. Never print at a UPS store, an office share, or a friend's house. If someone else can see the documents come out of the printer, you are already out of compliance.",
      },
      { type: "h3", text: "2. Locked transport" },
      {
        type: "p",
        text: "Documents move from your printer to the signer, and from the signer to the courier drop. They live in a locked case, a locked vehicle, or in your physical possession — never in a passenger seat with the windows down, never overnight in a hotel lobby.",
      },
      { type: "h3", text: "3. No copies for you" },
      {
        type: "p",
        text: "Do not keep a copy of the signed loan documents. Once the courier accepts the package, you are done. Any scan you make of the executed package for your own records is a potential GLBA violation.",
      },
      { type: "h3", text: "4. Journal enough, but not too much" },
      {
        type: "p",
        text: "Log the notarial acts, not the loan details. Your journal records that you notarized an acknowledgment for John Smith on a Deed of Trust — not the loan amount, not the interest rate, not the account number.",
      },
      { type: "h3", text: "5. Destroy remnants securely" },
      {
        type: "p",
        text: "Test pages, misprints, and confidential drafts must be shredded — not thrown in a trash bag on Wednesday morning. A cross-cut shredder is a compliance tool, not an office accessory.",
      },
      {
        type: "callout",
        title: "The NNA background check",
        text: "The annual NNA background screening exists because lenders require signing agents to be verifiably GLBA-safe. If you let your NNA screening lapse, most title companies cannot legally hire you.",
      },
      { type: "h2", text: "Why This Matters More in 2026" },
      {
        type: "p",
        text: "Data privacy enforcement has accelerated. The Consumer Financial Protection Bureau, state attorneys general, and title insurance underwriters are increasingly asking service providers — including notaries — for proof of GLBA-compliant handling. A signing agent who can articulate GLBA in a two-sentence answer stands out. A signing agent who cannot is quietly rotated out.",
      },
      { type: "h2", text: "The Simplest Test" },
      {
        type: "p",
        text: "Before you leave a signing, look around the room and your vehicle. Ask yourself: if the lender's compliance officer walked in right now, would they see confidential customer information exposed? If the answer is anything other than a firm no, you are not yet compliant. Fix it before the courier arrives.",
      },
    ],
  },
];

export function getArticleBySlug(slug) {
  return BLOG_ARTICLES.find((a) => a.slug === slug) || null;
}

export function getRelatedArticles(currentSlug, limit = 3) {
  return BLOG_ARTICLES.filter((a) => a.slug !== currentSlug).slice(0, limit);
}
